import React, { useMemo, useCallback } from 'react'
import {
	ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import type { IHydrateFormationChartData } from '../../interfaces/interfaces';
import { useTranslation } from 'react-i18next';
import { SINGLE_RESPONSIVE_CONTAINER_MIN_SIZE } from '../../constants/constants';


const HydrateFormationChart: React.FC<{ data: IHydrateFormationChartData[] }> = ({ data }) => {
	const isEmpty = data.length === 0;
	const { minP, maxP, minT, maxT, xTicks, yTicks } = useMemo(() => {
		if (isEmpty) {
			const minT = -20, maxT = 20, minP = 0, maxP = 40;
			const xTicks = [...new Set(Array.from({ length: 5 }, (_, i) => minT + (maxT - minT) * i / 4))];
			const yTicks = [...new Set(Array.from({ length: 5 }, (_, i) => minP + (maxP - minP) * i / 4))];
			return { minP: minP, maxP: maxP, minT: minT, maxT: maxT, xTicks: xTicks, yTicks: yTicks };
		}
		const minP = Math.min(...data.map(d => d.pressure));
		const maxP = Math.max(...data.map(d => d.pressure));
		const minT = Math.min(...data.map(d => d.temperature));
		const maxT = Math.max(...data.map(d => d.temperature));
		const xTicks = [...new Set(Array.from({ length: 5 }, (_, i) => minT + (maxT - minT) * i / 4))];
		const yTicks = [...new Set(Array.from({ length: 5 }, (_, i) => minP + (maxP - minP) * i / 4))];
		return { minP, maxP, minT, maxT, xTicks, yTicks };
	}, [data, isEmpty]);

	const tooltipLabelFormatter = useCallback((value: number) => value.toFixed(3), []);
	const tooltipFormatter = useCallback((value: number) => [value.toFixed(3)], []);
	const { t } = useTranslation("hydrates");

	return (
		<div className="hydrates__chart_container">
			<ResponsiveContainer width="100%" height={380} {...SINGLE_RESPONSIVE_CONTAINER_MIN_SIZE}>
				<ComposedChart data={isEmpty ? [{ pressure: 0, senoman: 0, towler_mokhatab: 0, valanjin: 0 }] : data} layout="vertical" margin={{
					top: 20,
					right: 30,
					left: 30,
					bottom: 80
				}}>
					<CartesianGrid strokeDasharray="0"
						horizontal={true}
						vertical={true}
					/>
					<XAxis
						axisLine={true}
						type="number"
						domain={[minT, maxT]}
						label={{ value: t("charts_labels.x"), position: 'insideBottom', offset: -10 }}
						tickFormatter={(value: number) => value.toFixed(0)}
						ticks={xTicks}
					/>
					<YAxis
						axisLine={true}
						dataKey="pressure"
						domain={[minP, maxP]}
						type="number"
						ticks={yTicks}
						reversed={true}
						label={{ value: t("charts_labels.y"), angle: -90, position: 'insideLeft', offset: -5 }}
						tickFormatter={(value: number) => value > 1000 ? value.toExponential(2) : value.toFixed(2)}
					/>
					<Tooltip
						labelFormatter={tooltipLabelFormatter}
						formatter={tooltipFormatter}
						wrapperStyle={{ pointerEvents: 'none' }}
						isAnimationActive={false}
					/>
					<Legend
						formatter={(value: number) => value}
						layout="horizontal"
						wrapperStyle={{ paddingTop: '15px' }}
					/>

					{/* <Area
						type="monotone"
						dataKey="senoman"
						fill="rgb(219, 107, 171)"
						fillOpacity={0.2}
						stroke="none"
						baseLine={maxT}
						isAnimationActive={false}
						legendType="none"
					/>*/}

					<Line
						type="monotone"
						dataKey="temperature"
						stroke={isEmpty ? "transparent" : "rgb(219, 107, 171)"}
						strokeWidth={3}
						name={t("charts_labels.senoman_line")}
						dot={false}
						activeDot={false}
						legendType="plainline"
					/>

				</ComposedChart>
			</ResponsiveContainer>
		</div >
	)
}

export default React.memo(HydrateFormationChart)